export const WIDGET_COLUMNS = {
  'performance_overall': 12,
  'performance_questions_answered': [8, 4],
  'performance_category': 4,
  'performance_possibility_of_passing': 4,
  'reports': 12,
};

function bestFitWidth([preferred, fallback], remainingColumns) {
  if (preferred <= remainingColumns) return preferred;
  if (fallback <= remainingColumns) return fallback;
  return preferred;
}

function formatRowClasses(rows) {
  return rows.flatMap(row => {
    if (row.length === 1 && row[0] === 12) return ['span-12']; // full-width widget gets span-12
    if (row.length === 1) return ['']; // no span class for a solo widget on a non-full-width row
    return row.map(col => `span-${col}`);
  });
}

/**
 * Compute CSS grid-column classes for a list of dashboard widgets.
 *
 * Widgets are laid out left-to-right into 12-column rows. Each widget type
 * declares its column width in {@link WIDGET_COLUMNS} — either a fixed number
 * or a `[preferred, fallback]` pair. When a preferred width would prevent the
 * next widget from fitting on the same row, the fallback width is used instead.
 *
 * Returns a flat array of class strings (one per widget, in order) such as
 * `'span-8'`, `'span-4'`, or `'span-12'`. A widget that sits alone on a
 * non-full-width row receives `''` (no span class).
 *
 * @param {Array<{widget: {widgetType: string}}>} displayedWidgets
 * @returns {string[]}
 */
export function computeWidgetClasses(displayedWidgets) {
  if (!displayedWidgets) return [];

  const rows = [];
  let columnsUsed = 0;

  for (let i = 0; i < displayedWidgets.length; i++) {
    const displayedWidget = displayedWidgets[i];
    const columnWidth = WIDGET_COLUMNS[displayedWidget.widget.widgetType];

    const nextWidget = displayedWidgets[i + 1];
    const nextColumnWidth = nextWidget ? WIDGET_COLUMNS[nextWidget.widget.widgetType] : null;
    const nextMinWidth = nextColumnWidth ? (Array.isArray(nextColumnWidth) ? nextColumnWidth[1] : nextColumnWidth) : 0;
    const remainingColumns = 12 - columnsUsed - nextMinWidth;

    const widgetColumns = Array.isArray(columnWidth)
      ? bestFitWidth(columnWidth, remainingColumns)
      : columnWidth;
    const currentRow = rows[rows.length - 1];

    if (!widgetColumns) {
      rows.push([0]);
      columnsUsed = 0;
    } else if (widgetColumns === 12) {
      rows.push([12]);
      columnsUsed = 0;
    } else if (!currentRow || currentRow[0] === 0 || currentRow[0] === 12) {
      rows.push([widgetColumns]);
      columnsUsed = widgetColumns;
    } else if (columnsUsed + widgetColumns > 12) {
      rows.push([widgetColumns]);
      columnsUsed = widgetColumns;
    } else {
      currentRow.push(widgetColumns);
      columnsUsed += widgetColumns;
    }
  }

  return formatRowClasses(rows);
}
